import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import type { PaymentMethod } from '@/types/paymentMethod';

const EMPTY_METHODS: PaymentMethod[] = [];

export const usePaymentMethods = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId } = useMultiTenant();

  const query = useQuery({
    queryKey: ['payment-methods', companyId],
    queryFn: async () => {
      if (!companyId) throw new Error('Company ID não disponível');
      
      // Lista completa (inclui inativos) — lookups de exibição precisam resolver
      // ids históricos de parcelas; consumidores selecionáveis recebem só ativos.
      // Cast via unknown: default_financial_account_id ainda não existe nos
      // types gerados (migration 145D-1a aplicada fora do ciclo de gen types).
      const { data, error } = await supabase
        .from('payment_methods')
        .select('id, company_id, name, code, description, default_financial_account_id, active, created_at, created_by, updated_at')
        .eq('company_id', companyId)
        .order('name');

      if (error) throw error;
      return (data || []) as unknown as PaymentMethod[];
    },
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: async (method: Omit<PaymentMethod, 'id' | 'created_at' | 'updated_at' | 'company_id'>) => {
      if (!companyId) throw new Error('Company ID não disponível');
      
      const { data, error } = await supabase
        .from('payment_methods')
        .insert([{ ...method, company_id: companyId }])
        .select()
        .single();

      if (error) throw error;
      return data as PaymentMethod;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      toast({ title: 'Forma de pagamento criada com sucesso' });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao criar forma de pagamento',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PaymentMethod> & { id: string }) => {
      const { data, error } = await supabase
        .from('payment_methods')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      toast({ title: 'Forma de pagamento atualizada com sucesso' });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar forma de pagamento',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('payment_methods')
        .update({ active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      toast({ title: 'Forma de pagamento excluída com sucesso' });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao excluir forma de pagamento',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    methods: query.data?.filter((m) => m.active) ?? EMPTY_METHODS,
    allMethods: query.data ?? EMPTY_METHODS,
    isLoading: query.isLoading,
    createMethod: createMutation.mutate,
    updateMethod: updateMutation.mutate,
    deleteMethod: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
